import { useEffect, useState } from "react";
import "../styles/RegisterPage.css";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../hooks/ThemeContext";
import { IndexHeaderNav } from "./IndexHeaderNav";
import * as Sentry from '@sentry/react';

function RegisterVerify() {
    const [error_message, setErrorMessage] = useState("");
    const [user_email, setUserEmail] = useState("");
    const [waiting, setWaiting] = useState(false);
    const [firstWait, setFirstWait] = useState(false);
    const [adminReg, setAdminReg] = useState(null);
    const { theme } = useTheme();
    const [notifyWaiting, setNotifyWaiting] = useState(false);
    const [notifySuccess, setNotifySuccess] = useState("none");

    const navigate = useNavigate();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        setAdminReg(params.get("type") === "admin");
    }, []);

    const HandleVerify = async (event) => {
        event.preventDefault();
        setWaiting(true);
        setErrorMessage("");

        try {
            const result = await fetch("/api/verify_email", {
                method: "POST",
                body: new FormData(event.target),
            });

            // If server responded but with error status
            if (!result.ok) {
                // statusText is often useless; give real info
                const text = await result.text().catch(() => "");
                throw new Error(text || result.statusText || `HTTP ${result.status}`);
            }

            const data = await result.json();

            if (data.redirect) {
                window.location.href = data.redirect;
                return;
            }

            if (data.error_message) {
                setErrorMessage(data.error_message);
                return;
            }

            setErrorMessage("Verified, but no redirect was provided.");
        } catch (err) {
            // This catches ERR_NETWORK_CHANGED / Failed to fetch
            setErrorMessage(
                "Network error detected. Try logging in. (" +
                    (err?.message || "Failed to fetch") +
                    ")"
            );
            Sentry.captureException(err);
        } finally {
            setWaiting(false);
        }
    };

    const HandleAdminVerify = async (event) => {
        event.preventDefault();
        setWaiting(true);
        setErrorMessage("");

        try {
            const result = await fetch("/api/admin_verify_email", {
                method: "POST",
                body: new FormData(event.target),
            });

            const data = await result.json();

            if (!result.ok) {
                setErrorMessage(data.error_message || `Request failed (${result.status})`);
                return;
            }

            if (data.redirect) {
                window.location.href = data.redirect;
                return;
            }

            if (data.error_message) {
                setErrorMessage(data.error_message);
                return;
            }

            setErrorMessage("Verified, but no redirect was provided.");
        } catch (err) {
            setErrorMessage('Network error. Please try again.');
            Sentry.captureException(err);
        } finally {
            setWaiting(false);
        }
    };

    async function notifyVerificationError(email) {
        try {
            const response = await fetch("/api/notify_verification_not_received", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            const result = await response.json();

            if (result.status === "success") {
                setNotifySuccess("success");
                setNotifyWaiting(false);
            } else {
                setNotifySuccess("error");
                setNotifyWaiting(false);
            }
        } catch (error) {
            setNotifySuccess("error");
            setNotifyWaiting(false);
            Sentry.captureException(error);
        }
    }

    useEffect(() => {
        if (adminReg !== null) {
            const endpoint = adminReg ? "/api/admin_verify_email" : "/api/verify_email";

            (async () => {
                try {
                    const response = await fetch(endpoint);
                    const data = await response.json();

                    if (!response.ok) {
                        setErrorMessage(data.error_message || 'Failed to load. Please try again.');
                        return;
                    }

                    if (data.user_email) {
                        setUserEmail(data.user_email);
                    }

                    if (data.error_message) {
                        setErrorMessage(data.error_message);
                    }
                } catch (e) {
                    setErrorMessage('Network error. Please try again.');
                    Sentry.captureException(e);
                } finally {
                    setFirstWait(true);
                }
            })();
        }
    }, [adminReg]);

    const headerTitle = adminReg ? "Admin Verify Email" : "Verify Email";

    return (
        <div className="register-page">
            <IndexHeaderNav />
            <div className="register-shell">
                <div className="register-card">
                    <div className="register-header">
                        <div className="register-header-left">
                            <button
                                className="back-btn"
                                onClick={() => navigate("/")}
                                type="button"
                            >
                                ← Back
                            </button>
                        </div>

                        <div className="register-header-center">
                            <h1>{headerTitle}</h1>
                        </div>

                        <div className="register-header-right">
                            {adminReg && (
                                <img
                                    src={theme.logo}
                                    alt="Rezify Logo"
                                    className="admin-logo"
                                />
                            )}
                        </div>
                    </div>

                    <div className="verify-wrap">
                        <p className="muted">
                            {!firstWait && "Loading verification info…"}
                            {firstWait && (
                                <>
                                    Enter the verification code sent to:{" "}
                                    <span className="mono">{user_email}</span>
                                </>
                            )}
                        </p>

                        <p className="muted">
                            It may take a few minutes to recieve the email.
                        </p>

                        <form
                            method="post"
                            className="register-form"
                            onSubmit={(event) => {
                                adminReg ? HandleAdminVerify(event) : HandleVerify(event);
                            }}
                        >
                            {!firstWait && (
                                <div className="center-row">
                                    <div className="spinner-black" />
                                </div>
                            )}

                            <div className="field">
                                <label>Verification Code</label>
                                <input
                                    type="text"
                                    name="code"
                                    aria-label="Verification code"
                                    placeholder="6-digit code"
                                    required
                                    minLength={6}
                                    maxLength={6}
                                />
                            </div>

                            {error_message.length >= 1 && (
                                <div className="error-box">{error_message}</div>
                            )}

                            <button
                                type="submit"
                                className="primary-btn"
                                disabled={waiting}
                            >
                                {waiting ? (
                                    <span className="btn-inline">
                                        <span className="spinner" />
                                    </span>
                                ) : (
                                    "Verify"
                                )}
                            </button>
                        </form>

                        <p className="muted center-text">
                            Didn&apos;t get the email? Check spam/junk.
                        </p>

                        <button
                            className="link-btn-code-again"
                            type="button"
                            onClick={() => {
                                window.location.href = adminReg ? "/verify_email?type=admin" : "/verify_email";
                            }}
                        >
                            Send Code Again
                        </button>

                        <div className="register-divider" />

                        <div className="support-row">
                            {notifySuccess !== "error" && notifySuccess !== "success" && (
                                <>
                                    <span className="support-text">
                                        Still not receiving email?
                                    </span>

                                    {!notifyWaiting && (
                                        <button
                                            type="button"
                                            className="support-button"
                                            disabled={notifyWaiting}
                                            onClick={() => {
                                                setNotifyWaiting(true);
                                                notifyVerificationError(user_email);
                                            }}
                                        >
                                            Notify Support
                                        </button>
                                    )}

                                    {notifyWaiting && <span className="support-spinner" />}

                                    <span className="tooltip-container">
                                        <span className="tooltip-icon">?</span>
                                        <span className="tooltip-text">
                                            Sends a support notification to the Rezify team to help resolve email verification issues.
                                            Your email address ({user_email}) will be included so we can assist you directly.
                                        </span>
                                    </span>
                                </>
                            )}

                            {notifySuccess === "error" && (
                                <span className="support-text">
                                    Error sending notification. Please try again later, or email us directly at support@rezify.ai
                                </span>
                            )}

                            {notifySuccess === "success" && (
                                <span className="support-text">
                                    Support has been notified! We will be at your assistance shortly.
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export { RegisterVerify };
